/**
 * SHEIN 同名「编号/重复」必填属性分组。
 *
 * 背景（测试反馈）：官方产品模板里会同时出现两个必填「主料克重」，
 * 平台侧其实只要求填其中一项，但我们的提交校验把两项都当独立必填，
 * 只填一个就报「请填写必填属性」，无法上架。
 *
 * TEMU / TikTok 已有等价能力（temuAttrVisibility / tiktokAttrVisibility 的
 * siblingRequiredGroupKey + collectEffectivelyRequiredAttrs），SHEIN 之前缺失，
 * 这里补齐同一语义：同组只「至少填一项」，UI 上填了一项后另一项不再标必填星号。
 */
import { attributeDisplayLabel } from '@/shein-platform/utils/sheinAttributeTemplate';
import { isFilledProductAttributeValue } from '@/shein-platform/utils/sheinProductAttributeTemplate';

/** 需要参与同名分组的属性：克重/重量类（平台常用 主料克重、里料克重1/2 等） */
const SIBLING_GROUP_NAME_HINT = /克重|重量|gsm|weight|g\/m|g\/㎡/i;

function stripBrackets(text) {
  return String(text || '')
    .replace(/\([^)]*\)/g, '')
    .replace(/（[^）]*）/g, '')
    .replace(/\[[^\]]*\]/g, '')
    .replace(/【[^】]*】/g, '');
}

/**
 * 分组基名：去单位括号、去末尾编号、去分隔符后小写。
 * 「主料克重 (g/m²)」→ 主料克重；「面料克重1」「面料克重2」→ 面料克重。
 */
export function sheinAttrGroupBaseName(attr) {
  const label = attributeDisplayLabel(attr) || attr?.name || attr?.nameEn || '';
  return stripBrackets(label)
    .replace(/[\s_-]*[\d０-９]+\s*$/u, '')
    .replace(/[\s\-_/·・.。，,]/g, '')
    .toLowerCase();
}

/**
 * 同名组 key：只有克重/重量类、且去编号后基名一致才会同名。
 * 返回空串表示「独立必填」，不参与分组。
 */
export function sheinSiblingRequiredGroupKey(attr) {
  const base = sheinAttrGroupBaseName(attr);
  if (!base || base.length < 2) return '';
  if (!SIBLING_GROUP_NAME_HINT.test(base)) return '';
  return `sib:${base}`;
}

export function isSheinAttrFilled(attr, productAttributes = {}) {
  const id = String(attr?.attributeId ?? '').trim();
  if (!id) return false;
  return isFilledProductAttributeValue(productAttributes?.[id]);
}

const ATTR_DEF_POOL_KEYS = ['productAttrs', 'requiredProduct', 'mainAttrs', 'otherAttrs'];

/** 在类目属性模板里按 attributeId 找定义（关联必填属性可能不在 requiredProduct 里） */
export function findSheinAttrDefById(attrTpl, attributeId) {
  const id = String(attributeId ?? '').trim();
  if (!id || !attrTpl || typeof attrTpl !== 'object') return null;
  for (const key of ATTR_DEF_POOL_KEYS) {
    const rows = Array.isArray(attrTpl[key]) ? attrTpl[key] : [];
    const hit = rows.find((attr) => String(attr?.attributeId ?? '') === id);
    if (hit) return hit;
  }
  return null;
}

function linkedAttrIdList(linkedAttrRules) {
  if (!linkedAttrRules) return [];
  if (linkedAttrRules instanceof Map) return [...linkedAttrRules.keys()].map(String);
  if (Array.isArray(linkedAttrRules)) return linkedAttrRules.map(String);
  if (typeof linkedAttrRules === 'object') return Object.keys(linkedAttrRules).map(String);
  return [];
}

/**
 * 本次提交真正需要校验的必填属性 = 平台 status=3 必填 + 关联规则必填，按 attributeId 去重。
 */
export function collectSheinEffectiveRequiredAttrs(attrTpl, linkedAttrRules) {
  const out = [];
  const seen = new Set();
  const push = (attr) => {
    const id = String(attr?.attributeId ?? '').trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(attr);
  };
  (Array.isArray(attrTpl?.requiredProduct) ? attrTpl.requiredProduct : []).forEach(push);
  linkedAttrIdList(linkedAttrRules).forEach((id) => {
    const key = String(id || '').trim();
    if (!key || seen.has(key)) return;
    const def = findSheinAttrDefById(attrTpl, key);
    if (def) push(def);
    else push({ attributeId: key, name: `属性 ${key}`, required: true });
  });
  return out;
}

/**
 * 校验用拆分：singles 为需逐项校验的必填；groups 为「至少填一项」的同名组（已满足的组不返回）。
 */
export function splitSheinRequiredAttrs(allRequiredAttrs = [], productAttributes = {}) {
  const byGroup = new Map();
  const singles = [];
  (Array.isArray(allRequiredAttrs) ? allRequiredAttrs : []).forEach((attr) => {
    const id = String(attr?.attributeId ?? '').trim();
    if (!id) return;
    const groupKey = sheinSiblingRequiredGroupKey(attr);
    if (!groupKey) {
      singles.push(attr);
      return;
    }
    if (!byGroup.has(groupKey)) byGroup.set(groupKey, []);
    byGroup.get(groupKey).push(attr);
  });

  const groups = [];
  byGroup.forEach((members, key) => {
    if (members.length <= 1) {
      singles.push(...members);
      return;
    }
    if (members.some((attr) => isSheinAttrFilled(attr, productAttributes))) return;
    groups.push({
      key,
      members,
      label: members
        .map((attr) => attributeDisplayLabel(attr) || attr.name || attr.attributeId)
        .filter(Boolean)
        .join(' / '),
    });
  });

  return { singles, groups };
}

/**
 * 参与同名分组（组内 ≥2 项）的 attributeId：这些项不逐项要求必填，
 * 由「至少填一项」的组校验统一处理。
 */
export function collectSheinSiblingGroupedIds(allRequiredAttrs = []) {
  const byGroup = new Map();
  (Array.isArray(allRequiredAttrs) ? allRequiredAttrs : []).forEach((attr) => {
    const id = String(attr?.attributeId ?? '').trim();
    if (!id) return;
    const groupKey = sheinSiblingRequiredGroupKey(attr);
    if (!groupKey) return;
    if (!byGroup.has(groupKey)) byGroup.set(groupKey, []);
    byGroup.get(groupKey).push(id);
  });
  const ids = new Set();
  byGroup.forEach((members) => {
    if (members.length <= 1) return;
    members.forEach((id) => ids.add(id));
  });
  return ids;
}

/**
 * 同组内已满足（有其他成员已填）但本身未填的 attributeId：
 * 关联规则校验据此放行，避免「填了一项还报另一项必填」。
 */
export function collectSheinSiblingSatisfiedIds(allRequiredAttrs = [], productAttributes = {}) {
  const byGroup = new Map();
  (Array.isArray(allRequiredAttrs) ? allRequiredAttrs : []).forEach((attr) => {
    const id = String(attr?.attributeId ?? '').trim();
    if (!id) return;
    const groupKey = sheinSiblingRequiredGroupKey(attr);
    if (!groupKey) return;
    if (!byGroup.has(groupKey)) byGroup.set(groupKey, []);
    byGroup.get(groupKey).push(attr);
  });

  const satisfied = new Set();
  byGroup.forEach((members) => {
    if (members.length <= 1) return;
    if (!members.some((attr) => isSheinAttrFilled(attr, productAttributes))) return;
    members.forEach((attr) => {
      const id = String(attr?.attributeId ?? '').trim();
      if (id && !isSheinAttrFilled(attr, productAttributes)) satisfied.add(id);
    });
  });
  return satisfied;
}

/**
 * UI：该属性是否还显示必填星号。
 * 同名组里已有任一项填写后，未填项不再强调必填（组内只需一项）。
 */
export function isSheinAttrRequiredMark(attr, allRequiredAttrs = [], productAttributes = {}) {
  const groupKey = sheinSiblingRequiredGroupKey(attr);
  if (!groupKey) return true;
  const siblings = (Array.isArray(allRequiredAttrs) ? allRequiredAttrs : [])
    .filter((item) => sheinSiblingRequiredGroupKey(item) === groupKey);
  if (siblings.length <= 1) return true;
  return !siblings.some((item) => isSheinAttrFilled(item, productAttributes));
}
